import React, { type FC, useState } from 'react';
import css from './TemplatesGrid.scss';
import {
  Badge,
  Box,
  Button,
  Card,
  Heading,
  Image,
  MarketingLayout,
  Tag,
  Text,
} from '@wix/design-system';
import type {
  AppTemplate,
  Tag as TemplateTag,
} from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { DataHooks } from '../../../constants/dataHooks';
import type { OnTemplateClickFunc } from './TemplatesGrid';
import { tabsByIdMapper } from '../../../utils/mappers';
import { useTranslation } from '@wix/wix-i18n-config';
import {
  isBlocksScratchTemplate,
  isCliScratchTemplate,
  isSelfHostedScratchTemplate,
} from '../../../utils/templateSelector/templatesSelector';
import { ExternalLink } from '@wix/wix-ui-icons-common';

type GridItemProps = {
  template: AppTemplate;
  index: number;
  onTemplateClick: OnTemplateClickFunc;
};

export const TemplateItem: FC<GridItemProps> = ({
  template,
  index,
  onTemplateClick,
}) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<boolean>(false);
  const badgeType = template.developmentTools?.[0];
  const buttonConfig = getButtonConfig();

  function getButtonConfig() {
    if (isCliScratchTemplate(template)) {
      return {
        text: t('app.grid.template-item.button.get-started'),
        icon: undefined,
      };
    }
    if (
      isBlocksScratchTemplate(template) ||
      isSelfHostedScratchTemplate(template)
    ) {
      return {
        text: t('app.grid.template-item.button.create-app'),
        icon: <ExternalLink />,
      };
    }
    return null;
  }

  return (
    <div
      role="button"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onTemplateClick(template)}
      data-hook={DataHooks.TemplateItemContainer(index)}
    >
      <Card
        className={css.gridCard}
        showShadow={hovered}
        dataHook={DataHooks.TemplateWrapper(template.id as string)}
      >
        <Badge
          uppercase={false}
          size="small"
          className={css.badge}
          dataHook={DataHooks.TemplateBadge(index)}
        >
          {t(tabsByIdMapper[badgeType]?.badgeTextKey)}
        </Badge>
        <MarketingLayout
          inverted
          size="tiny"
          alignItems="stretch"
          title={
            <Heading
              ellipsis
              size="medium"
              maxLines={2}
              className={css.reduceMarginLeft}
              dataHook={DataHooks.TemplateTitle(index)}
            >
              {template?.title}
            </Heading>
          }
          description={
            <div className={css.reduceMarginLeft}>
              <Text
                ellipsis
                secondary
                size="small"
                maxLines={2}
                dataHook={DataHooks.TemplateSubtitle(index)}
              >
                {template?.subtitle}
              </Text>
            </div>
          }
          actions={
            <Box
              gap="SP1"
              flexWrap="wrap"
              maxHeight="45px"
              overflow="hidden"
              className={css.reduceMarginLeft}
            >
              {hovered && buttonConfig && (
                <Box maxWidth="200px">
                  <Button
                    size="small"
                    skin="dark"
                    ellipsis
                    suffixIcon={buttonConfig.icon}
                    dataHook={DataHooks.TemplateGridItemButton(index)}
                  >
                    {buttonConfig.text}
                  </Button>
                </Box>
              )}
              {template?.tags?.map((tag: TemplateTag, tagIndex) => (
                <Tag
                  size="tiny"
                  theme="lightOutlined"
                  key={tag.title}
                  removable={false}
                  id={tagIndex.toString()}
                  dataHook={DataHooks.TemplateTag(tagIndex, index)}
                >
                  {tag.title}
                </Tag>
              ))}
            </Box>
          }
          imagePadding={false}
          image={
            <Image
              width={160}
              height={189}
              borderRadius="7px 0 0 7px"
              src={template?.thumbnail}
              className={css.templateItemImage}
              dataHook={DataHooks.TemplateGridItemThumbnailImage(index)}
            />
          }
        />
      </Card>
    </div>
  );
};
